/**
 * #4053 — full-pane content shown in the Preview tab when it's blocked
 * (no game loaded / build not past the phase gate / force-disabled by a
 * capability or local-readiness gate). Pure presentational: no IPC, no
 * new copy beyond what `previewBlockedPaneCopy` (`plan-build-handoff.ts`)
 * already derives from state the Workspace already computes.
 *
 * Replaces the icon-only header button's tooltip-only affordance — see
 * the plan's B.1/B.2 rationale: a disabled icon button hides "why" behind
 * a hover; the Preview tab is always clickable and this pane is what a
 * blocked click lands on.
 */
import { Lock } from 'lucide-react';

export interface PreviewBlockedPaneProps {
  headline: string;
  body: string;
}

export function PreviewBlockedPane({ headline, body }: PreviewBlockedPaneProps): React.JSX.Element {
  return (
    <div
      data-testid="preview-blocked-pane"
      className="flex h-full items-center justify-center bg-crucible-bg px-6"
    >
      <div className="flex max-w-md flex-col items-center gap-3 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.06] text-white/50">
          <Lock size={16} strokeWidth={1.75} />
        </div>
        <p
          data-testid="preview-blocked-pane-headline"
          className="text-base font-medium text-white/90"
        >
          {headline}
        </p>
        <p
          data-testid="preview-blocked-pane-body"
          className="text-sm leading-relaxed text-white/60"
        >
          {body}
        </p>
      </div>
    </div>
  );
}
